// /packages/shared-ui/utils/auth.ts
import { Amplify, type ResourcesConfig } from "aws-amplify";
import { type TokenProvider, decodeJWT } from "aws-amplify/auth";
import {
  getRawLocalStorageItem,
  removeLocalStorageItem,
  setRawLocalStorageItem,
} from "./localStorage";

// トークンを設定する
type Tokens = {
  tokenStoreName: string;
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
};

// トークンをローカルストレージに保存/取り出しする
const save = (tokens: Tokens) => {
  const { tokenStoreName, accessToken, idToken, refreshToken } = tokens;
  setRawLocalStorageItem(`${tokenStoreName}-accessToken`, accessToken);
  setRawLocalStorageItem(`${tokenStoreName}-idToken`, idToken || "");
  setRawLocalStorageItem(`${tokenStoreName}-refreshToken`, refreshToken || "");
};

const load = async (storeName: string) => {
  const access = getRawLocalStorageItem(`${storeName}-accessToken`);
  const id = getRawLocalStorageItem(`${storeName}-idToken`) || undefined;
  const refresh =
    getRawLocalStorageItem(`${storeName}-refreshToken`) || undefined;

  return { access, id, refresh };
};

// トークンを保存する
export const applyToken = (props: Tokens) => {
  // トークンをSessionStorageに保存
  const { tokenStoreName, accessToken, idToken, refreshToken } = props;
  save({ tokenStoreName, accessToken, idToken, refreshToken });
};

// ローカルストレージのトークンを削除する（ログアウト時に呼ぶ）
// 各プロダクトのログアウトはオリジンが異なる Portal をまたげないため、
// プロダクト自身がこの関数で自分のトークン (`${storeName}-accessToken` 等) を消す必要がある。
export const clearToken = (storeName: string) => {
  removeLocalStorageItem(`${storeName}-accessToken`);
  removeLocalStorageItem(`${storeName}-idToken`);
  removeLocalStorageItem(`${storeName}-refreshToken`);
};

export const setTokenProvider = (
  storeName: string,
  userPoolId: string,
  userPoolClientId: string,
  authRefreshUrl?: string,
) => {
  const tokenProvider = createTokenProvider(storeName, authRefreshUrl);

  //  tokenPrividerをAmplifyに設定
  const resourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
      },
    },
  } satisfies ResourcesConfig;

  Amplify.configure(resourcesConfig, {
    Auth: { tokenProvider },
  });
};

// Module-level refresh lock to prevent concurrent refresh requests
const refreshLocks = new Map<
  string,
  Promise<{ access: string; id: string; refresh?: string }>
>();

// Amplifyが使用するTokenProvider
// export しているのはユニットテスト（auth.test.ts）から直接叩くため。
// アプリからは setTokenProvider 経由で使うこと。
export const createTokenProvider = (
  storeName: string,
  authRefreshUrl?: string,
): TokenProvider => {
  return {
    async getTokens({ forceRefresh } = {}) {
      const cached = await load(storeName);
      if (!cached || !cached.access) return null; // 未ログイン扱い

      // トークンの有効期限チェック
      const accessToken = decodeJWT(cached.access);
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = accessToken.payload.exp as number;
      const shouldRefresh = expiresAt - now < 300; // 5分前

      // リフレッシュが必要かつリフレッシュURLが提供されている場合
      if ((forceRefresh || shouldRefresh) && authRefreshUrl && cached.refresh) {
        // Check if refresh is already in progress for this store
        const existingRefresh = refreshLocks.get(storeName);
        if (existingRefresh) {
          console.log("Refresh already in progress, waiting...");
          try {
            await existingRefresh;
            // After refresh completes, reload tokens from storage
            const refreshed = await load(storeName);
            if (!refreshed || !refreshed.access) return null;
            return {
              accessToken: decodeJWT(refreshed.access),
              idToken: refreshed.id ? decodeJWT(refreshed.id) : undefined,
              refreshToken: refreshed.refresh,
            };
          } catch (error) {
            console.error("Concurrent refresh failed:", error);
            return null;
          }
        }

        // Start refresh with lock
        const refreshPromise = (async () => {
          try {
            const { callAuthRefresh } = await import("./auth-api");
            const newTokens = await callAuthRefresh(
              cached.refresh!,
              authRefreshUrl,
            );
            save({
              tokenStoreName: storeName,
              accessToken: newTokens.access,
              idToken: newTokens.id,
              refreshToken: newTokens.refresh || cached.refresh, // Preserve old if not provided
            });
            return newTokens;
          } finally {
            refreshLocks.delete(storeName);
          }
        })();

        refreshLocks.set(storeName, refreshPromise);

        try {
          const newTokens = await refreshPromise;
          return {
            accessToken: decodeJWT(newTokens.access),
            idToken: decodeJWT(newTokens.id),
            refreshToken: newTokens.refresh || cached.refresh,
          };
        } catch (error) {
          console.error("Token refresh failed:", error);
          // PRODUCT_DEV-797: 401 = リフレッシュトークンが失効している（期限切れ・
          // グローバルサインアウト等）。ここで消さずに null だけ返していたため、
          // 次の getTokens() が同じ死んだトークンを読み直し、通知ポーリング(30秒)や
          // RUM のクレデンシャル更新(50分)がタブを開いている限り叩き続けていた。
          //
          // 401 以外（タイムアウト・オフライン・500）では消さない。一時的な失敗で
          // 強制ログアウトさせないため。
          if ((error as { status?: number } | null)?.status === 401) {
            // 別タブが先にリフレッシュを成功させてトークンを差し替えていたら消さない
            // （生きているセッションの巻き添えを防ぐ）。
            const latest = await load(storeName);
            if (latest.refresh === cached.refresh) {
              clearToken(storeName);
            }
          }
          return null; // リフレッシュ失敗 → 再ログイン
        }
      }

      // リフレッシュ不要な場合はキャッシュから返す
      return {
        accessToken: decodeJWT(cached.access),
        idToken: cached.id ? decodeJWT(cached.id) : undefined,
        refreshToken: cached.refresh,
      };
    },
  };
};
